/**
 * Custom hook to fetch dynamic menu from API
 * API: /gateway/registration/userrolescreenpermission/get-menus/{roleid}/{tenantid}
 */

import { useState, useEffect } from 'react';
import { apiRequest } from '../services/apiClient';
import authService from '../api/authService';
import { FEATURES_LIST_UPDATED_EVENT } from '../components/globalfeatures';

export const useDynamicMenu = () => {
  const [menuData, setMenuData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMenu = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get current user to extract roleId and tenantId
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        console.warn('⚠️ No current user found, cannot fetch menu');
        setError('User not authenticated');
        setLoading(false);
        return;
      }
      
      // Get roleId and tenantId
      const roleId = currentUser.roleId;
      const tenantId = currentUser.tenantId || 
                       parseInt(sessionStorage.getItem('tenantId') || '1', 10);
      
      if (!roleId) {
        console.warn('⚠️ No roleId found for current user');
        setError('User role not found');
        setLoading(false);
        return;
      }
      
      //console.log('🔄 Fetching menu from API...', { roleId, tenantId });

      const [menuResult, featureResult] = await Promise.allSettled([
        apiRequest(`/gateway/registration/userrolescreenpermission/get-menus/${roleId}/${tenantId}`, {
          method: 'GET'
        }),
        apiRequest(`/gateway/registration/userrolefeaturepermission/by-role/${roleId}/${tenantId}`, {
          method: 'GET'
        }),
      ]);

      if (menuResult.status === 'rejected') {
        throw menuResult.reason;
      }
      const response = menuResult.value;

      let featureList = [];
      if (featureResult.status === 'fulfilled') {
        const featurePermResponse = featureResult.value;
        if (Array.isArray(featurePermResponse)) {
          featureList = featurePermResponse;
        } else if (Array.isArray(featurePermResponse?.response)) {
          featureList = featurePermResponse.response;
        } else if (Array.isArray(featurePermResponse?.data)) {
          featureList = featurePermResponse.data;
        } else if (Array.isArray(featurePermResponse?.data?.data)) {
          featureList = featurePermResponse.data.data;
        }
      } else {
        console.warn('⚠️ Feature permissions fetch failed:', featureResult.reason);
      }
      sessionStorage.setItem('featuresList', JSON.stringify(featureList));
      window.dispatchEvent(new CustomEvent(FEATURES_LIST_UPDATED_EVENT));

      //console.log('📦 Raw API response:', response);
      
      // Handle response structure: { status: "success", response: [...] }
      let menu = [];
      if (Array.isArray(response)) {
        menu = response;
      } else if (response?.response && Array.isArray(response.response)) {
        menu = response.response;
      } else if (response?.data && Array.isArray(response.data)) {
        menu = response.data;
      } else if (response?.data?.data && Array.isArray(response.data.data)) {
        menu = response.data.data;
      }
      
      //console.log('📋 Menu data before normalization:', menu);
      
      // Normalize menu structure - Map from get-menus API structure to expected format
      // API structure: { moduleId, moduleCode, moduleDesc, subMenu: [{ screen_id, screen_name, screen_label, routing, can_view, can_insert, can_update, can_delete }] }
      menu = menu.map(module => {
        const normalizedModule = {
          id: module.moduleId,
          menumoduleName: module.moduleCode || module.moduleDesc || module.menumoduleName || module.moduleName || module.name,
          isActive: module.isActive !== undefined ? module.isActive : (module.is_active !== undefined ? module.is_active : true),
          submenu: (module.subMenu || module.submenu || []).map(screen => ({
            id: screen.screen_id || screen.id, // Use screen_id from API
            screenLable: screen.screen_label || screen.screenLable || screen.screenLabel || screen.Screen_label || screen.label,
            screenName: screen.screen_name || screen.screenName || screen.Screen_name || screen.name,
            routing: screen.routing || screen.route || screen.path,
            canView: screen.can_view !== undefined ? Boolean(screen.can_view) : (screen.canView !== undefined ? Boolean(screen.canView) : true),
            canInsert: screen.can_insert !== undefined ? Boolean(screen.can_insert) : (screen.canInsert !== undefined ? Boolean(screen.canInsert) : false),
            canUpdate: screen.can_update !== undefined ? Boolean(screen.can_update) : (screen.canUpdate !== undefined ? Boolean(screen.canUpdate) : false),
            canDelete: screen.can_delete !== undefined ? Boolean(screen.can_delete) : (screen.canDelete !== undefined ? Boolean(screen.canDelete) : false),
            isActive: screen.isActive !== undefined ? screen.isActive : (screen.is_active !== undefined ? screen.is_active : true),
            isDashboard: screen.isDashboard !== undefined ? screen.isDashboard : (screen.is_dashboard !== undefined ? screen.is_dashboard : false)
          }))
        };
        //console.log(`📁 Module "${normalizedModule.menumoduleName}": ${normalizedModule.submenu.length} screens`);
        return normalizedModule;
      });
      
      //console.log('✅ Normalized menu data:', menu);
      setMenuData(menu);
      try {
        sessionStorage.setItem('hdDynamicMenuCache', JSON.stringify(menu));
      } catch {
        /* ignore quota / private mode */
      }
    } catch (err) {
      console.error('❌ Error fetching menu:', err);
      setError(err.message || 'Failed to load menu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch if user is authenticated
    if (authService.isAuthenticated()) {
      fetchMenu();
    } else {
      setLoading(false);
    }
    
    // Refetch on login
    const handleLogin = () => {
      if (authService.isAuthenticated()) {
        fetchMenu();
      }
    };
    window.addEventListener('user-logged-in', handleLogin);
    return () => window.removeEventListener('user-logged-in', handleLogin);
  }, []);

  return { menuData, loading, error, refetch: fetchMenu };
};
